import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Calendar, Package } from 'lucide-react';
import { format, addDays, startOfDay } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Production estimates per flush day (in kg) - User provided values
// Flush 1: 5.5 tonnes total, Flush 2: 3.5 tonnes total
const FLUSH_1_PRODUCTION = {
  1: 500,   // Day 1: 500kg
  2: 1500,  // Day 2: 1500kg
  3: 2500,  // Day 3: 2500kg
  4: 1000,  // Day 4: 1000kg
};

const FLUSH_2_PRODUCTION = {
  1: 400,   // Day 1: 400kg
  2: 1200,  // Day 2: 1200kg
  3: 1200,  // Day 3: 1200kg
  4: 700,   // Day 4: 700kg
};

const FLUSH_1_TOTAL = 5500; // 5.5 tonnes as per user
const FLUSH_2_TOTAL = 3500; // 3.5 tonnes as per user

const ProductionForecast = () => {
  const [roomCycles, setRoomCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forecast5Days, setForecast5Days] = useState([]);
  const [forecast15Days, setForecast15Days] = useState([]);
  const [forecast30Days, setForecast30Days] = useState([]);
  const [totals, setTotals] = useState({ next5Days: 0, next15Days: 0, next30Days: 0 });
  const [flushTotals, setFlushTotals] = useState({
    flush1_5days: 0, flush2_5days: 0,
    flush1_15days: 0, flush2_15days: 0,
    flush1_30days: 0, flush2_30days: 0,
    flush1_rooms_5days: 0, flush2_rooms_5days: 0,
    flush1_rooms_15days: 0, flush2_rooms_15days: 0,
    flush1_rooms_30days: 0, flush2_rooms_30days: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const cyclesRes = await axios.get(`${API}/room-cycles?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const roomsRes = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle both array and object with 'cycles' key response formats
      const cycles = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      const rooms = roomsRes.data;
      
      // Create room lookup
      const roomLookup = {};
      rooms.forEach(r => { roomLookup[r.id] = r.name; });
      
      setRoomCycles(cycles.map(c => ({
        ...c,
        room_name: roomLookup[c.room_id] || 'Unknown'
      })));
      
      // Calculate forecasts
      calculateForecasts(cycles, roomLookup);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateForecasts = (cycles, roomLookup) => {
    const today = startOfDay(new Date());
    const in5Days = addDays(today, 5);
    const in15Days = addDays(today, 15);
    const in30Days = addDays(today, 30);
    
    const forecast5 = [];
    const forecast15 = [];
    const forecast30 = [];
    let total5 = 0;
    let total15 = 0;
    let total30 = 0;
    
    // Flush-wise tracking
    let flush1_5 = 0, flush2_5 = 0;
    let flush1_15 = 0, flush2_15 = 0;
    let flush1_30 = 0, flush2_30 = 0;
    let flush1_rooms_5 = new Set(), flush2_rooms_5 = new Set();
    let flush1_rooms_15 = new Set(), flush2_rooms_15 = new Set();
    let flush1_rooms_30 = new Set(), flush2_rooms_30 = new Set();
    
    cycles.forEach(cycle => {
      const roomName = roomLookup[cycle.room_id] || 'Unknown';
      
      // Get flush dates
      const flush1Start = cycle.flush1_start_date ? new Date(cycle.flush1_start_date) : null;
      const flush2Start = cycle.flush2_start_date ? new Date(cycle.flush2_start_date) : null;
      
      // Check Flush 1 (4 days)
      if (flush1Start) {
        for (let day = 1; day <= 4; day++) {
          const harvestDate = addDays(flush1Start, day - 1);
          const production = FLUSH_1_PRODUCTION[day];
          
          // Check if within next 5 days
          if (harvestDate >= today && harvestDate <= in5Days) {
            forecast5.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 1',
              day: day,
              production: production
            });
            total5 += production;
            flush1_5 += production;
            flush1_rooms_5.add(roomName);
          }
          
          // Check if within next 15 days
          if (harvestDate >= today && harvestDate <= in15Days) {
            forecast15.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 1',
              day: day,
              production: production
            });
            total15 += production;
            flush1_15 += production;
            flush1_rooms_15.add(roomName);
          }
          
          // Check if within next 30 days
          if (harvestDate >= today && harvestDate <= in30Days) {
            forecast30.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 1',
              day: day,
              production: production
            });
            total30 += production;
            flush1_30 += production;
            flush1_rooms_30.add(roomName);
          }
        }
      }
      
      // Check Flush 2 (4 days)
      if (flush2Start) {
        for (let day = 1; day <= 4; day++) {
          const harvestDate = addDays(flush2Start, day - 1);
          const production = FLUSH_2_PRODUCTION[day];
          
          // Check if within next 5 days
          if (harvestDate >= today && harvestDate <= in5Days) {
            forecast5.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 2',
              day: day,
              production: production
            });
            total5 += production;
            flush2_5 += production;
            flush2_rooms_5.add(roomName);
          }
          
          // Check if within next 15 days
          if (harvestDate >= today && harvestDate <= in15Days) {
            forecast15.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 2',
              day: day,
              production: production
            });
            total15 += production;
            flush2_15 += production;
            flush2_rooms_15.add(roomName);
          }
          
          // Check if within next 30 days
          if (harvestDate >= today && harvestDate <= in30Days) {
            forecast30.push({
              date: harvestDate,
              room: roomName,
              batch: cycle.batch_name,
              flush: 'Flush 2',
              day: day,
              production: production
            });
            total30 += production;
            flush2_30 += production;
            flush2_rooms_30.add(roomName);
          }
        }
      }
    });
    
    // Sort by date
    forecast5.sort((a, b) => a.date - b.date);
    forecast15.sort((a, b) => a.date - b.date);
    forecast30.sort((a, b) => a.date - b.date);
    
    setForecast5Days(forecast5);
    setForecast15Days(forecast15);
    setForecast30Days(forecast30);
    setTotals({ next5Days: total5, next15Days: total15, next30Days: total30 });
    setFlushTotals({
      flush1_5days: flush1_5, flush2_5days: flush2_5,
      flush1_15days: flush1_15, flush2_15days: flush2_15,
      flush1_30days: flush1_30, flush2_30days: flush2_30,
      flush1_rooms_5days: flush1_rooms_5.size, flush2_rooms_5days: flush2_rooms_5.size,
      flush1_rooms_15days: flush1_rooms_15.size, flush2_rooms_15days: flush2_rooms_15.size,
      flush1_rooms_30days: flush1_rooms_30.size, flush2_rooms_30days: flush2_rooms_30.size
    });
  };

  // Group forecasts by date for daily summary
  const groupByDate = (forecasts) => {
    const grouped = {};
    forecasts.forEach(f => {
      const dateKey = format(f.date, 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: f.date,
          items: [],
          totalProduction: 0
        };
      }
      grouped[dateKey].items.push(f);
      grouped[dateKey].totalProduction += f.production;
    });
    return Object.values(grouped).sort((a, b) => a.date - b.date);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading forecast...</div>;
  }

  const grouped5Days = groupByDate(forecast5Days);
  const grouped15Days = groupByDate(forecast15Days);
  const grouped30Days = groupByDate(forecast30Days);

  // Helper to render flush breakdown
  const renderFlushBreakdown = (flush1Total, flush2Total, flush1Rooms, flush2Rooms, period) => {
    const total = flush1Total + flush2Total;
    const flush1Pct = total > 0 ? ((flush1Total / total) * 100).toFixed(0) : 0;
    const flush2Pct = total > 0 ? ((flush2Total / total) * 100).toFixed(0) : 0;
    
    return (
      <div className="grid grid-cols-2 gap-3 mb-4" data-testid={`flush-breakdown-${period}`}>
        <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-indigo-800">Flush 1</span>
            <Badge className="bg-indigo-100 text-indigo-700">{flush1Rooms} room{flush1Rooms !== 1 ? 's' : ''}</Badge>
          </div>
          <p className="text-xl font-bold text-indigo-900">{(flush1Total / 1000).toFixed(1)} t</p>
          <p className="text-xs text-indigo-600">{flush1Total.toLocaleString()} kg ({flush1Pct}%)</p>
          <p className="text-xs text-indigo-500 mt-1">~5.5t/room × {flush1Rooms} = {(5.5 * flush1Rooms).toFixed(1)}t est.</p>
        </div>
        <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-violet-800">Flush 2</span>
            <Badge className="bg-violet-100 text-violet-700">{flush2Rooms} room{flush2Rooms !== 1 ? 's' : ''}</Badge>
          </div>
          <p className="text-xl font-bold text-violet-900">{(flush2Total / 1000).toFixed(1)} t</p>
          <p className="text-xs text-violet-600">{flush2Total.toLocaleString()} kg ({flush2Pct}%)</p>
          <p className="text-xs text-violet-500 mt-1">~3.5t/room × {flush2Rooms} = {(3.5 * flush2Rooms).toFixed(1)}t est.</p>
        </div>
      </div>
    );
  };

  // Render a forecast list for a given grouped data
  const renderForecastList = (groupedData, emptyMessage, badgeColor) => {
    if (groupedData.length === 0) {
      return <div className="text-center py-8 text-gray-500">{emptyMessage}</div>;
    }
    return (
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {groupedData.map((dayGroup, idx) => (
          <div key={idx} className="border rounded-lg p-3" data-testid={`forecast-day-${format(dayGroup.date, 'yyyy-MM-dd')}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">{format(dayGroup.date, 'EEE, MMM d')}</span>
                <Badge className={badgeColor}>
                  {dayGroup.items.length} room{dayGroup.items.length > 1 ? 's' : ''}
                </Badge>
              </div>
              <span className="font-bold text-emerald-600">{dayGroup.totalProduction.toLocaleString()} kg</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {dayGroup.items.map((item, i) => (
                <div key={i} className="text-sm bg-gray-50 p-2 rounded flex justify-between">
                  <span>
                    {item.room} - {item.flush} Day {item.day}
                  </span>
                  <span className="font-medium">{item.production} kg</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="production-forecast">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#1A4D2E]" />
          Expected Production Forecast
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-600 font-medium">Next 5 Days</p>
            <p className="text-2xl font-bold text-orange-800">{(totals.next5Days / 1000).toFixed(1)} tonnes</p>
            <p className="text-xs text-orange-500">{totals.next5Days.toLocaleString()} kg</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-600 font-medium">Next 15 Days</p>
            <p className="text-2xl font-bold text-blue-800">{(totals.next15Days / 1000).toFixed(1)} tonnes</p>
            <p className="text-xs text-blue-500">{totals.next15Days.toLocaleString()} kg</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-sm text-emerald-600 font-medium">Next 30 Days</p>
            <p className="text-2xl font-bold text-emerald-800">{(totals.next30Days / 1000).toFixed(1)} tonnes</p>
            <p className="text-xs text-emerald-500">{totals.next30Days.toLocaleString()} kg</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600 font-medium">Per Room Yield</p>
            <p className="text-sm text-gray-700">Flush 1: ~{(FLUSH_1_TOTAL/1000).toFixed(1)} tonnes</p>
            <p className="text-sm text-gray-700">Flush 2: ~{(FLUSH_2_TOTAL/1000).toFixed(1)} tonnes</p>
          </div>
        </div>

        {/* Detailed Forecast Tabs */}
        <Tabs defaultValue="5days" className="w-full">
          <TabsList className="mb-4 grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="5days" data-testid="tab-5days">
              5 Days
            </TabsTrigger>
            <TabsTrigger value="15days" data-testid="tab-15days">
              15 Days
            </TabsTrigger>
            <TabsTrigger value="30days" data-testid="tab-30days">
              30 Days
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="5days">
            <div className="mb-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                <span className="font-medium text-orange-800">5-Day Forecast</span>
                <span className="ml-auto text-lg font-bold text-orange-600">{totals.next5Days.toLocaleString()} kg</span>
              </div>
            </div>
            {renderFlushBreakdown(flushTotals.flush1_5days, flushTotals.flush2_5days, flushTotals.flush1_rooms_5days, flushTotals.flush2_rooms_5days, '5days')}
            {renderForecastList(grouped5Days, 'No harvest expected in the next 5 days', 'bg-orange-100 text-orange-800')}
          </TabsContent>
          
          <TabsContent value="15days">
            <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-800">15-Day Forecast</span>
                <span className="ml-auto text-lg font-bold text-blue-600">{totals.next15Days.toLocaleString()} kg</span>
              </div>
            </div>
            {renderFlushBreakdown(flushTotals.flush1_15days, flushTotals.flush2_15days, flushTotals.flush1_rooms_15days, flushTotals.flush2_rooms_15days, '15days')}
            {renderForecastList(grouped15Days, 'No harvest expected in the next 15 days', 'bg-blue-100 text-blue-800')}
          </TabsContent>
          
          <TabsContent value="30days">
            <div className="mb-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-600" />
                <span className="font-medium text-emerald-800">30-Day Forecast</span>
                <span className="ml-auto text-lg font-bold text-emerald-600">{totals.next30Days.toLocaleString()} kg</span>
              </div>
            </div>
            {renderFlushBreakdown(flushTotals.flush1_30days, flushTotals.flush2_30days, flushTotals.flush1_rooms_30days, flushTotals.flush2_rooms_30days, '30days')}
            {renderForecastList(grouped30Days, 'No harvest expected in the next 30 days', 'bg-emerald-100 text-emerald-800')}
          </TabsContent>
        </Tabs>
        
        {/* Production breakdown info */}
        <div className="mt-6 pt-4 border-t text-xs text-gray-500">
          <p className="font-medium mb-2">Daily Production Breakdown (per room):</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-medium text-gray-700">Flush 1 (~5.5 tonnes total)</p>
              <p>Day 1: 500 kg | Day 2: 1,500 kg | Day 3: 2,500 kg | Day 4: 1,000 kg</p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Flush 2 (~3.5 tonnes total)</p>
              <p>Day 1: 400 kg | Day 2: 1,200 kg | Day 3: 1,200 kg | Day 4: 700 kg</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductionForecast;
